import React from 'react';
import { View } from 'react-native';
import THEME from '../../../config/theme';
import { FontAwesome } from '@expo/vector-icons';
import { ViewComment, CommentBody, CommentHeader } from '../style';
import { SmallText, FooterText } from '../../../config/theme/globalStyles';
import { TouchableOpacity } from 'react-native';
import { useRoute } from '@react-navigation/native';
import { usePublicUsers, useUser } from '../../../queries/userQuery';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { hexToRgba } from '../../../utils/colorUtils';
import { timeAgo } from '../../../utils/dateUtils';
import CommentAvatar from '../CommentAvatar';
import { useCommentTypography } from '../useCommentTypography';

const ReplyItem = ({ reply, handleDeleteReply, comment, user }) => {
  const { data: userData } = useUser();
  const { customColor } = useCustomTheme();
  const route = useRoute();

  // Só a Community usa este id (o post ao qual a resposta pertence). Na aula
  // `route.params` existe mas não tem `id` — sem o `?.` uma rota sem params
  // derrubava a linha.
  const postId = route?.params?.id;

  const authorsById = usePublicUsers([reply?.userId]);
  const author = authorsById.get(reply?.userId);

  const authorName =
    reply?.authorName || reply?.userName || author?.name || 'Aluno';
  const authorPhoto = reply?.authorPhoto || author?.photo || null;
  const authorIsAdmin =
    reply?.authorIsAdmin === true || author?.isAdmin === true;

  const textColor = customColor?.text?.app || THEME.COLORS.TEXT_MAIN;
  const muted = hexToRgba(textColor, 0.6) || '#6B7280';

  const { fontTitle, fontText, fontSM, fontXS, lineHeight } =
    useCommentTypography();

  const createdAt = timeAgo(reply?.createdAt) || 'agora';

  // Fallbacks de campo: a Community grava `reply`, a aula grava `comment`.
  const text = reply?.comment || reply?.reply || reply?.replies;

  const canDelete = userData?.isAdmin === true || user?.id === reply?.userId;

  return (
    // Igual ao CommentItem: renderiza sempre, mesmo sem conseguir resolver o
    // autor — a resposta existe e precisa aparecer.
    <ViewComment style={{ paddingVertical: 8 }}>
      <CommentAvatar
        size={28}
        photo={authorPhoto}
        isAdmin={authorIsAdmin}
        label={`Foto de ${authorName}`}
      />

      <CommentBody style={{ marginLeft: 10 }}>
        <CommentHeader>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            <FooterText
              color={textColor}
              textAlign="left"
              numberOfLines={1}
              style={{ fontFamily: fontTitle, fontSize: fontXS, flexShrink: 1 }}
            >
              {authorName}
            </FooterText>
            <FooterText
              color={muted}
              style={{ fontFamily: fontText, fontSize: fontXS, marginLeft: 6 }}
            >
              {`· ${createdAt}`}
            </FooterText>
          </View>

          {canDelete && (
            <TouchableOpacity
              onPress={() =>
                handleDeleteReply?.(
                  reply.commentId || comment?.id,
                  reply.id,
                  postId
                )
              }
              hitSlop={12}
              accessibilityRole="button"
              accessibilityLabel={`Excluir resposta de ${authorName}`}
              style={{ paddingLeft: 8, cursor: 'pointer' }}
            >
              <FontAwesome name="trash-o" size={14} color={muted} />
            </TouchableOpacity>
          )}
        </CommentHeader>

        <SmallText
          color={textColor}
          textAlign="left"
          style={{
            fontFamily: fontText,
            fontSize: fontSM,
            // A resposta é um degrau abaixo do comentário, como já era.
            lineHeight: lineHeight - 2,
            marginTop: 2,
          }}
        >
          {text}
        </SmallText>
      </CommentBody>
    </ViewComment>
  );
};

export default React.memo(ReplyItem);
